import React, { useState, useEffect } from "react";
import { getFirestore, doc, setDoc, getDoc, getDocs, updateDoc, arrayUnion, Timestamp, where, collection, query } from "firebase/firestore";

import { Header } from "../../components/Header/Header";
import { useUser } from "../../context/UserContext";
import { MyHours } from "../../components/MyHours/MyHours";
import { MySchedule } from "../../components/MySchedule/MySchedule";
import { DateRangePicker } from "../../components/DatePicker/DatePicker";
import { EmployeeSchedule, TimeEvent } from "../../types/employee";
import { calculateWorkedMinutesWithOvernights } from "../../utils/utils";
import { updateAccruedMinutesForDate } from "../../utils/accrual";
import { getClockInAccess } from "../../utils/deviceIdentity";

import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';


type WorkedRangeResult = {
    totalMinutes: number;
    dailyBreakdown: { date: string; minutesWorked: number }[];
};

type EmployeeWorkedMap = {
    [employeeId: string]: {
        [date: string]: number; // minutesWorked for the week 
    };
};

/* An overview of this component. On load or date range change, we will fetch all time_entries and working schedule for the user in the given date range.
 We will then calculate the total minutes worked and the breakdown of minutes by day.
 during that calculation we will grab the events from today and udpate state so that the UI can reflect the current clock state.
 this component also allows the user to clock in, clock out, take a break, and end a break and updates firestore accordingly. */
export const Dashboard: React.FC = () => {
    const { userData, loading } = useUser();
    const db = getFirestore();
    dayjs.extend(isoWeek);
    dayjs.extend(weekday);

    //State variables to manage stuff
    const [clockedIn, setClockIn] = useState(false);
    const [onBreak, setOnBreak] = useState(false);
    const [breakText, setBreakText] = useState("Take a Break");
    const [thisWeeksSchedule, setThisWeeksSchedule] = useState<EmployeeSchedule[]>([]);
    const [userTimeEntryMap, setUserTimeEntryMap] = useState<Record<string, { events: TimeEvent[]; minutesWorked: number }>>({});
    const [clockedInAt, setClockedInTime] = useState("");
    const [scheduledMinutes, setScheduledMinutes] = useState<number>(0);
    const [dateRange, setDateRange] = React.useState<{
        startDate: Dayjs | null;
        endDate: Dayjs | null;
    }>(() => {
        const today = dayjs();
        const startOfWeek = today.weekday(0); // Sunday
        const endOfWeek = today.weekday(6);   // Saturday
        return {
            startDate: startOfWeek,
            endDate: endOfWeek,
        };
    });

    useEffect(() => {
        const handleVisibilityChange = () => {
            
            if (document.visibilityState === "visible") {
                const fetchTime = async () => {
                    if (!userData) return 

                    if (!dateRange.startDate || !dateRange.endDate || !userData.id) return;

                    const newTimeEntryMap: Record<
                        string,
                        { events: TimeEvent[]; minutesWorked: number }
                    > = {};

                    const start = dateRange.startDate;
                    const end = dateRange.endDate;

                    // Generate all dates in range and initialize map
                    let current = start.clone();
                    while (current.isSameOrBefore(end, "day")) {
                        const dateStr = current.format("YYYY-MM-DD");
                        newTimeEntryMap[dateStr] = { events: [], minutesWorked: 0 };
                        current = current.add(1, "day");
                    }

                    // Fetch events including an extra day for overnight shifts
                    const endPlusOne = end.add(1, "day").format("YYYY-MM-DD");
                    const startStr = start.format("YYYY-MM-DD");

                    const q = query(
                        collection(db, "time_entries"),
                        where("userId", "==", userData.id),
                        where("date", ">=", startStr),
                        where("date", "<=", endPlusOne)
                    );

                    const snapshot = await getDocs(q);

                    let allEvents: TimeEvent[] = [];
                    let todayEvents: TimeEvent[] = [];
                    let yesterdayEvents: TimeEvent[] = [];

                    const todayStr = dayjs().format("YYYY-MM-DD");
                    const yesterdayStr = dayjs().subtract(1, "day").format("YYYY-MM-DD");

                    snapshot.docs.forEach((doc) => {
                        const data = doc.data();
                        const date = data.date;
                        const events: TimeEvent[] = Array.isArray(data.events) ? data.events : [];

                        // Sort events by timestamp
                        const sortedEvents = events.sort(
                            (a, b) => (a.timestamp?.toMillis?.() ?? 0) - (b.timestamp?.toMillis?.() ?? 0)
                        );

                        allEvents.push(...sortedEvents);

                        if (date >= startStr && date <= end.format("YYYY-MM-DD")) {
                            newTimeEntryMap[date].events = sortedEvents;
                        }

                        if (date === todayStr) {
                            todayEvents = sortedEvents;
                            hydrateTodayState(sortedEvents);
                        }

                        if (date === yesterdayStr) {
                            yesterdayEvents = sortedEvents;
                        }
                    });

                    //if there are no events today look at yesterdays events and hydrate the state for those events
                    //this helps with overnight shifts so if they haven't yet clocked out and its 2:am itll still read as clocked in
                    if (todayEvents.length === 0 && yesterdayEvents.length > 0) {
                        hydrateTodayState(yesterdayEvents)
                    }

                    // Calculate minutes with overnight logic
                    const minutesByDay = calculateWorkedMinutesWithOvernights(allEvents);

                    // Merge minutes back into map
                    for (const [date, entry] of Object.entries(newTimeEntryMap)) {
                        entry.minutesWorked = minutesByDay[date] ?? 0;
                    }

                    setUserTimeEntryMap(newTimeEntryMap);
                };

                fetchTime();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [userData?.id]);

    //this gets all the scheduled days and calculates the scheduled time to work
    useEffect(() => {
        //make sure our data is available before using it to get docs
        if (!userData?.id) return;

        if (!dateRange.startDate || !dateRange.endDate) return;
        //this gets all the documents for the current date range selected in the schedules collection
        const getUserScheduleForRange = async (userId: string, startDate: Date, endDate: Date) => {
            const start = startDate.toISOString().split("T")[0];
            const end = endDate.toISOString().split("T")[0];

            //this query gets all schedules for the user in the given date range in one call to firestore
            //so that we aviod making multiple calls, one for each day in the range
            const q = query(
                collection(db, "schedules"),
                where("userId", "==", userId),
                where("date", ">=", start),
                where("date", "<=", end)
            );
            const snapshot = await getDocs(q);
            const schedules = snapshot.docs.map((doc) => doc.data() as EmployeeSchedule)
            //sets the schedule so we can render it in the UI
            setThisWeeksSchedule(schedules);

            // Calculate total scheduled minutes
            let totalScheduledMinutes = 0;
            schedules.forEach((schedule) => {
                if (schedule.location !== "Time Off" && schedule.location !== "Pto") {
                    const start = schedule.shiftStart.toDate();
                    const end = schedule.shiftEnd.toDate();
                    const minutes = (end.getTime() - start.getTime()) / (1000 * 60); // convert ms to minutes
                    totalScheduledMinutes += minutes;
                }
            });

            // Save or log the value
            setScheduledMinutes(totalScheduledMinutes);
        };

        //this sets an array of EmployeeSchedule objects for the current user which is date shift start etc.
        getUserScheduleForRange(userData.id, dateRange.startDate.toDate(), dateRange.endDate.toDate())

        const fetchTime = async () => {
            if (!dateRange.startDate || !dateRange.endDate || !userData.id) return;

            const newTimeEntryMap: Record<
                string,
                { events: TimeEvent[]; minutesWorked: number }
            > = {};

            const start = dateRange.startDate;
            const end = dateRange.endDate;

            // Generate all dates in range and initialize map
            let current = start.clone();
            while (current.isSameOrBefore(end, "day")) {
                const dateStr = current.format("YYYY-MM-DD");
                newTimeEntryMap[dateStr] = { events: [], minutesWorked: 0 };
                current = current.add(1, "day");
            }

            // Fetch events including an extra day for overnight shifts
            const endPlusOne = end.add(1, "day").format("YYYY-MM-DD");
            const startStr = start.format("YYYY-MM-DD");

            const q = query(
                collection(db, "time_entries"),
                where("userId", "==", userData.id),
                where("date", ">=", startStr),
                where("date", "<=", endPlusOne)
            );

            const snapshot = await getDocs(q);

            let allEvents: TimeEvent[] = [];
            let todayEvents: TimeEvent[] = [];
            let yesterdayEvents: TimeEvent[] = [];

            const todayStr = dayjs().format("YYYY-MM-DD");
            const yesterdayStr = dayjs().subtract(1, "day").format("YYYY-MM-DD");

            snapshot.docs.forEach((doc) => {
                const data = doc.data();
                const date = data.date;
                const events: TimeEvent[] = Array.isArray(data.events) ? data.events : [];

                // Sort events by timestamp
                const sortedEvents = events.sort(
                    (a, b) => (a.timestamp?.toMillis?.() ?? 0) - (b.timestamp?.toMillis?.() ?? 0)
                );

                allEvents.push(...sortedEvents);

                if (date >= startStr && date <= end.format("YYYY-MM-DD")) {
                    newTimeEntryMap[date].events = sortedEvents;
                }

                if (date === todayStr) {
                    todayEvents = sortedEvents;
                    hydrateTodayState(sortedEvents);
                }

                if (date === yesterdayStr) {
                    yesterdayEvents = sortedEvents;
                }
            });

            //if there are no events today look at yesterdays events and hydrate the state for those events
            //this helps with overnight shifts so if they haven't yet clocked out and its 2:am itll still read as clocked in
            if (todayEvents.length === 0 && yesterdayEvents.length > 0) {
                hydrateTodayState(yesterdayEvents)
            }

            // Calculate minutes with overnight logic
            const minutesByDay = calculateWorkedMinutesWithOvernights(allEvents);

            // Merge minutes back into map
            for (const [date, entry] of Object.entries(newTimeEntryMap)) {
                entry.minutesWorked = minutesByDay[date] ?? 0;
            }

            setUserTimeEntryMap(newTimeEntryMap);
        };

        fetchTime();


    }, [userData?.id, dateRange.startDate, dateRange.endDate]);


    //this takes in the events from today in firestore and hydrates the state variables
    const hydrateTodayState = (events: TimeEvent[]) => {
        // Sort events by timestamp descending (most recent first)
        const sortedEvents = [...events].sort((a, b) => {
            const aTime = a.timestamp.toDate().getTime()

            const bTime = b.timestamp.toDate().getTime()

            return bTime - aTime;
        });

        const mostRecent = sortedEvents[0];
        const firstEvent = sortedEvents[sortedEvents.length - 1];

        if (firstEvent.type === "clock_in") {

            let date = firstEvent.timestamp.toDate();

            const formattedTime = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

            if (mostRecent.type !== "clock_out" && mostRecent.type !== "break_start") setClockedInTime(formattedTime);
        }

        if (mostRecent.type === "clock_out") setClockIn(false)
        if (mostRecent.type === "clock_in") setClockIn(true)
        if (mostRecent.type === "break_start") {
            setClockIn(true)
            setOnBreak(true)
            setBreakText("Get Back to Work")
        }
        if (mostRecent.type === "break_end") setClockIn(true)

        if (mostRecent.type === "clock_out") {
            setClockIn(false)
            setClockedInTime("")
        }

    }

    //function to update the ui and initate the db call
    const handleClockIn = () => {
        if (clockedIn && !onBreak) {
            alert("You're already working!")
            return
        }

        if (clockedIn && onBreak) {
            alert("Looks like you are on break, click the Get Back To Work button to clock back in!")
            return
        }

        if (!clockedIn && !onBreak && userData != null) {
            ///api call to log clock in time
            clockIn(userData.id)
            //state functions for ui
        }

    };

    //function to update the ui and initate the db call
    const handleGoOnBreak = () => {

        if (!clockedIn) {
            alert("You must clock in before taking a break.");
            return;
        }

        if (onBreak && userData !== null) {
            endBreak(userData.id);
            return
        }

        if (!onBreak && userData !== null) {
            takeBreak(userData.id);
        }

    };

    //function to update the ui and initate the db call
    const handleClockOut = () => {
        //thanks dalyn
        // if (!clockedIn) {
        //     alert("You must clock in before clocking out")
        // }

        if (userData !== null && clockedIn) {
            clockOut(userData.id);
        }

    };

    //takes a given date object and returns it in MST 
    const getTimeZoneStringFromDate = (convertDate: Date): string => {
        const now = convertDate
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: 'America/Denver', // MST/MDT (with DST handling)
        };

        // format: MM/DD/YYYY
        const formatter = new Intl.DateTimeFormat('en-US', options);
        const parts = formatter.formatToParts(now);
        const year = parts.find(p => p.type === 'year')?.value;
        const month = parts.find(p => p.type === 'month')?.value;
        const day = parts.find(p => p.type === 'day')?.value;
        const today = `${year}-${month}-${day}`; // "YYYY-MM-DD" format
        return today
    }


    //functions handling database interactions
    const getTodayDocRef = (userId: string) => {

        const today = getTimeZoneStringFromDate(new Date());

        const docId = `${userId}_${today}`;
        return doc(db, "time_entries", docId);
    }

    //db call to clock user in
    async function clockIn(userId: string) {
        const docRef = getTodayDocRef(userId);
        const now = Timestamp.now();
        const formattedTime = now.toDate().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        const todayDateString = getTimeZoneStringFromDate(new Date());

        try {
            const access = await getClockInAccess(
                db,
                userData?.remoteWorkApproved === true
            );

            if (!access.allowed) {
                if (access.reason === "settings_unavailable") {
                    alert("Unable to verify this device. Please try again.");
                } else {
                    alert("You must use an approved office kiosk to clock in.");
                }
                return;
            }

            const clockInEvent: TimeEvent = {
                type: "clock_in",
                timestamp: now,
                source: access.source,
                ...(access.deviceId ? { deviceId: access.deviceId } : {}),
                ...(access.deviceLocation ? { deviceLocation: access.deviceLocation } : {}),
            };

            await updateAccruedMinutesForDate(db, userId, todayDateString, async () => {
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    // Create the doc if it doesn't exist yet
                    await setDoc(docRef, {
                        userId,
                        date: todayDateString,
                        events: [clockInEvent],
                    });
                } else {
                    // Add a new event to the existing array
                    await updateDoc(docRef, {
                        events: arrayUnion(clockInEvent),
                    });
                }
            });

            // Only update state if Firestore operation succeeded
            setClockIn(true);
            setOnBreak(false);
            setClockedInTime(formattedTime);

        } catch (error) {
            alert("Failed to clock in please try again")
            // Optionally show error message to user
        }
    }

    //db call to take a break
    async function takeBreak(userId: string) {
        const docRef = getTodayDocRef(userId);
        const now = Timestamp.now();
        const todayDateString = getTimeZoneStringFromDate(new Date());

        try {
            const access = await getClockInAccess(
                db,
                userData?.remoteWorkApproved === true
            );

            if (!access.allowed) {
                if (access.reason === "settings_unavailable") {
                    alert("Unable to verify this device. Please try again.");
                } else {
                    alert("You must use an approved office kiosk to start a break.");
                }
                return;
            }

            const breakStartEvent: TimeEvent = {
                type: "break_start",
                timestamp: now,
                source: access.source,
                ...(access.deviceId ? { deviceId: access.deviceId } : {}),
                ...(access.deviceLocation ? { deviceLocation: access.deviceLocation } : {}),
            };

            await updateAccruedMinutesForDate(db, userId, todayDateString, async () => {
                await updateDoc(docRef, {
                    events: arrayUnion(breakStartEvent)
                });
            });

            setOnBreak(true);
            setBreakText("Get Back to Work");
            setClockedInTime("");
        } catch (error) {
            alert("Failed to start break please try again");
        }
    }

    //db call to end a break
    async function endBreak(userId: string) {
        const docRef = getTodayDocRef(userId);
        const now = Timestamp.now();
        const todayDateString = getTimeZoneStringFromDate(new Date());

        try {
            const access = await getClockInAccess(
                db,
                userData?.remoteWorkApproved === true
            );

            if (!access.allowed) {
                if (access.reason === "settings_unavailable") {
                    alert("Unable to verify this device. Please try again.");
                } else {
                    alert("You must use an approved office kiosk to end a break.");
                }
                return;
            }

            const breakEndEvent: TimeEvent = {
                type: "break_end",
                timestamp: now,
                source: access.source,
                ...(access.deviceId ? { deviceId: access.deviceId } : {}),
                ...(access.deviceLocation ? { deviceLocation: access.deviceLocation } : {}),
            };

            await updateAccruedMinutesForDate(db, userId, todayDateString, async () => {
                await updateDoc(docRef, {
                    events: arrayUnion(breakEndEvent)
                });
            });

            setOnBreak(false);
            setBreakText("Take a Break");
            setClockedInTime("");
        } catch (error) {
            alert("Failed to end break please try again");
        }
    }

    //db call to clock out
    async function clockOut(userId: string) {
        const docRef = getTodayDocRef(userId);
        const now = Timestamp.now();
        const todayDateString = getTimeZoneStringFromDate(new Date());

        try {
            const access = await getClockInAccess(
                db,
                userData?.remoteWorkApproved === true
            );

            if (!access.allowed) {
                if (access.reason === "settings_unavailable") {
                    alert("Unable to verify this device. Please try again.");
                } else {
                    alert("You must use an approved office kiosk to clock out.");
                }
                return;
            }

            const clockOutEvent: TimeEvent = {
                type: "clock_out",
                timestamp: now,
                source: access.source,
                ...(access.deviceId ? { deviceId: access.deviceId } : {}),
                ...(access.deviceLocation ? { deviceLocation: access.deviceLocation } : {}),
            };

            await updateAccruedMinutesForDate(db, userId, todayDateString, async () => {
                await updateDoc(docRef, {
                    events: arrayUnion(clockOutEvent)
                });
            });

            setClockIn(false);
            setOnBreak(false);
            setClockedInTime("");
        } catch (error) {
            alert("Failed to clock out please try again");
        }
    }

    // Determine clock status based on user data
    let clockStyles = "status clocked-out";
    let clockText = "Clocked Out";

    if (loading) return <p>Loading...</p>;
    if (clockedIn) {
        clockStyles = "status clocked-in";
        clockText = "Clocked In";
    }
    if (!clockedIn) {
        clockStyles = "status clocked-out";
        clockText = "Clocked Out";
    }
    if (onBreak) {
        clockStyles = "status on-break";
        clockText = "On Break";
    }

    return (
        <div className="dashboard-container">
            <Header />

            <h2 className="welcome-message">Welcome {userData?.firstName}!</h2>

            <div className="date-picker">
                <DateRangePicker
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onChange={setDateRange}
                />
            </div>


            <main className="dashboard-content">

                <section className="panel">
                    <div className="panel-header">
                        <h2>Time Clock</h2>
                        <span className={clockStyles}>{clockText} {clockedInAt}</span>
                    </div>
                    <div className="panel-body time-controls">
                        <button className="btn" onClick={handleClockIn}>Clock In</button>
                        <button className="btn" onClick={handleGoOnBreak}>{breakText}</button>
                        <button className="btn" onClick={handleClockOut}>Clock Out</button>
                    </div>
                </section>

                <section className="panel">
                    <MyHours workBreakdown={userTimeEntryMap} />

                </section>

                <section className="panel">
                    <MySchedule
                        thisWeeksSchedule={thisWeeksSchedule} //this is dynamic to date range so not actually jut this week
                        startDate={dateRange.startDate?.toDate()}
                        endDate={dateRange.endDate?.toDate()}
                        scheduledMinutes={scheduledMinutes}
                    />
                </section>

            </main>

        </div>
    );
};

require('./styles.scss'); // Assuming you have a CSS file for styling
